import { useState } from "react";
import { X, Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AuthModal({ open, onClose, initialMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const isLogin = mode === "login";

  const onChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Account created — your plans live here now.");
      }
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10 bg-black/45 backdrop-blur-sm"
      data-testid="auth-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-teal-700">
              {isLogin ? "Welcome back" : "Create your account"}
            </div>
            <h2 className="font-display text-2xl font-semibold mt-1">
              {isLogin ? "Sign in" : "Save your floor plans"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="auth-modal-close"
            className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 pt-4 pb-6 space-y-4" data-testid="auth-form">
          {!isLogin && (
            <LabeledInput
              icon={User}
              label="Full Name"
              type="text"
              value={form.name}
              onChange={onChange("name")}
              placeholder="Jane Doe"
              required
              testId="auth-input-name"
            />
          )}
          <LabeledInput
            icon={Mail}
            label="Email"
            type="email"
            value={form.email}
            onChange={onChange("email")}
            placeholder="jane@example.com"
            required
            testId="auth-input-email"
          />
          <LabeledInput
            icon={Lock}
            label="Password"
            type="password"
            value={form.password}
            onChange={onChange("password")}
            placeholder={isLogin ? "Your password" : "Min. 6 characters"}
            required
            minLength={6}
            testId="auth-input-password"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="auth-submit-btn"
            className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Working…
              </>
            ) : (
              <>
                {isLogin ? "Sign in" : "Create account"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="text-xs text-center text-stone-500">
            {isLogin ? "New to DMV Floor Plans?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode"
              onClick={() => setMode(isLogin ? "register" : "login")}
              className="font-semibold text-teal-700 hover:underline"
            >
              {isLogin ? "Create one →" : "Sign in →"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function LabeledInput({ icon: Icon, label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-600 mb-1.5 block pl-1">
        {label}
      </span>
      <span className="relative block">
        <span className="absolute inset-y-0 left-3 flex items-center text-stone-400">
          <Icon className="h-4 w-4" />
        </span>
        <input
          {...rest}
          data-testid={testId}
          className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full pl-10 pr-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors"
        />
      </span>
    </label>
  );
}
